// SUB-OFFICE 7 — THE CASEFILE. Your Archive drawer, opened: filings with
// verdicts (paged 20), the stamp shelf, the standing seismograph (inline
// SVG over standing_events), the Character Assessment with its REVISED date,
// notifications, lineage, and the FILE TRANSFER CODE panel with the reclaim
// entry — identity resilience lives in the same drawer as the biography.

import { api } from '../net';
import { adoptIdentity, loadProfile, replaceProfile, type Profile } from '../identity';
import { holdSlamButton, memoWithQuotes, printLayer, redacted } from './chrome';
import type { EventBus } from '../entities/entities';
import type { CasefileResponse } from '../../shared/types';

export interface CasefileCallbacks {
  onReclaimed: () => Promise<void>;
}

export async function openCasefile(
  bus: EventBus,
  profile: Profile,
  cb: CasefileCallbacks
): Promise<void> {
  const layer = printLayer();
  const card = document.createElement('div');
  card.className = 'paper-modal wide letterhead-doc';
  card.innerHTML =
    `<div class="paper-head letterhead"><span>THE ARCHIVE — YOUR DRAWER</span><span>CASE FILE</span></div>`;
  const body = document.createElement('div');
  body.className = 'paper-body typewriter';
  body.innerHTML = '<div class="compose-note">THE DRAWER SLIDES OPEN. PAPER SETTLES.</div>';
  card.appendChild(body);
  const foot = document.createElement('div');
  foot.className = 'paper-foot';
  card.appendChild(foot);
  layer.appendChild(card);

  const close = (): void => card.remove();
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'slam-btn secondary';
  closeBtn.textContent = 'CLOSE THE DRAWER';
  closeBtn.addEventListener('click', close);
  foot.appendChild(closeBtn);

  const res = await api.casefile(0);
  if (!res.ok || !res.data) {
    body.innerHTML = '';
    body.textContent = res.memo || 'RECORDS ROOM TEMPORARILY SEALED.';
    renderTransferPanel(body, bus, profile, cb, close);
    return;
  }
  const d = res.data;
  body.innerHTML = '';

  // header
  const head = document.createElement('div');
  head.innerHTML =
    `<div style="font-size:15px">${esc(d.designation)} — STANDING ${d.standing}</div>` +
    `<div class="lh-sub letterhead">${esc(d.grade)} · CATEGORY: ${esc(d.category)} · ` +
    `PETITIONS ${d.petitions} · DENIALS ${d.denials}${d.probation ? ' · PROBATION' : ''}</div>`;
  body.appendChild(head);

  // character assessment
  if (d.character_assessment) {
    body.appendChild(rule());
    const a = document.createElement('div');
    a.innerHTML =
      `<div class="field-label">CHARACTER ASSESSMENT${d.assessment_revised_at
        ? ` — REVISED ${new Date(d.assessment_revised_at).toISOString().slice(0, 10)}` : ''}</div>` +
      `<div style="font-size:12px">${esc(d.character_assessment)}</div>`;
    body.appendChild(a);
  }

  // notifications
  for (const line of d.notifications?.lines || []) {
    const n = document.createElement('div');
    n.style.cssText = 'font-size:12px;color:var(--clemency);margin-top:4px';
    n.textContent = line;
    body.appendChild(n);
  }

  // DISPOSITIONS ON FILE (contract §23.2) — the machine's standing decisions
  // about the work. A cold list, each line as filed. Omitted entirely when
  // empty: the Bureau does not advertise absence.
  if (d.dispositions && d.dispositions.length) {
    body.appendChild(rule());
    const lbl = document.createElement('div');
    lbl.className = 'field-label';
    lbl.textContent = 'DISPOSITIONS ON FILE';
    body.appendChild(lbl);
    const note = document.createElement('div');
    note.style.cssText = 'font-size:11px;opacity:.7;margin-top:2px';
    note.textContent = 'WHAT THE MACHINE HAS DECIDED ABOUT YOUR WORK. IT IS INJECTED INTO EVERY JUDGE.';
    body.appendChild(note);
    for (const disp of d.dispositions) {
      const row = document.createElement('div');
      row.style.cssText = 'font-size:12px;margin-top:4px;padding-left:8px;border-left:2px solid var(--carbon)';
      row.textContent = disp;
      body.appendChild(row);
    }
  }

  // standing seismograph — inline SVG over the chronological series
  if (d.standing_series.length >= 2) {
    body.appendChild(rule());
    const lbl = document.createElement('div');
    lbl.className = 'field-label';
    lbl.textContent = 'STANDING SEISMOGRAPH';
    body.appendChild(lbl);
    body.appendChild(seismograph(d.standing_series));
  }

  // stamp shelf
  if (d.stamps.length) {
    body.appendChild(rule());
    const lbl = document.createElement('div');
    lbl.className = 'field-label';
    lbl.textContent = 'THE STAMP SHELF';
    body.appendChild(lbl);
    const shelf = document.createElement('div');
    shelf.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-top:6px';
    const seen = new Set<string>();
    for (const s of d.stamps) {
      if (seen.has(s.stamp)) continue;
      seen.add(s.stamp);
      const chip = document.createElement('span');
      chip.className = 'stamp stamp-ink';
      chip.style.cssText = 'font-size:10px;padding:3px 8px;transform:rotate(-2deg)';
      chip.textContent = s.stamp;
      chip.title = s.note || '';
      shelf.appendChild(chip);
    }
    body.appendChild(shelf);
  }

  // filings
  body.appendChild(rule());
  const flabel = document.createElement('div');
  flabel.className = 'field-label';
  flabel.textContent = `FILINGS (${d.petitions} ON RECORD)`;
  body.appendChild(flabel);
  const list = document.createElement('div');
  body.appendChild(list);
  let cursor: number | null = d.filings_cursor;
  const renderFilings = (rows: CasefileResponse['filings']): void => {
    for (const f of rows) {
      const row = document.createElement('div');
      row.style.cssText = 'border:1px solid rgba(28,26,23,.35);padding:6px 9px;margin-top:6px;font-size:12px';
      const v = f.verdict;
      row.innerHTML =
        `<div class="field-label">${esc(f.kind)}${f.genre ? ' — ' + esc(f.genre) : ''} · ` +
        `${new Date(f.created_at).toISOString().slice(0, 10)}${v ? ` · ${esc(v.stamp)}` : ''}</div>`;
      const txt = document.createElement('div');
      txt.style.opacity = '0.85';
      txt.appendChild(redacted(f.text.length > 160 ? f.text.slice(0, 160) + '…' : f.text));
      row.appendChild(txt);
      if (v) {
        const memo = document.createElement('div');
        memo.style.cssText = 'margin-top:3px;opacity:.75;font-size:11px';
        memo.appendChild(memoWithQuotes(v.memo, v.quotes || []));
        row.appendChild(memo);
      }
      list.appendChild(row);
    }
  };
  renderFilings(d.filings);
  if (cursor !== null) {
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'slam-btn secondary';
    more.style.cssText = 'margin-top:8px;font-size:10px;padding:8px 12px';
    more.textContent = 'PULL THE NEXT FOLDER';
    more.addEventListener('click', () => {
      void (async () => {
        if (cursor === null) return;
        const next = await api.casefile(cursor);
        if (next.ok && next.data) {
          renderFilings(next.data.filings);
          cursor = next.data.filings_cursor;
          if (cursor === null) more.remove();
        }
      })();
    });
    body.appendChild(more);
  }

  // lineage
  if (d.lineage && d.lineage.length) {
    body.appendChild(rule());
    const lbl = document.createElement('div');
    lbl.className = 'field-label';
    lbl.textContent = 'LINEAGE';
    body.appendChild(lbl);
    for (const l of d.lineage) {
      const row = document.createElement('div');
      row.style.cssText = 'font-size:12px;margin-top:3px';
      row.textContent = `${l.designation} — ${l.status}${l.epitaph ? ` — ${l.epitaph}` : ''}`;
      body.appendChild(row);
    }
  }

  renderTransferPanel(body, bus, profile, cb, close);
}

function renderTransferPanel(
  body: HTMLElement,
  bus: EventBus,
  profile: Profile,
  cb: CasefileCallbacks,
  close: () => void
): void {
  body.appendChild(rule());
  const lbl = document.createElement('div');
  lbl.className = 'field-label';
  lbl.textContent = 'FILE TRANSFER CODE';
  body.appendChild(lbl);
  const codeArea = document.createElement('div');
  codeArea.innerHTML = profile.claim_phrase && profile.ticket_no
    ? `<div class="cert-code tab-num">No. ${profile.ticket_no} — “${esc(profile.claim_phrase)}”</div>` +
      `<div style="font-size:11px;opacity:.8">YOUR FILE EXISTS. DO NOT MISPLACE YOURSELF.</div>`
    : `<div style="font-size:11px;opacity:.8">The code was shown once, at intake. The Bureau does not repeat itself.</div>`;
  body.appendChild(codeArea);

  const rl = document.createElement('div');
  rl.className = 'field-label';
  rl.style.marginTop = '8px';
  rl.textContent = 'RECLAIM A MISPLACED FILE';
  body.appendChild(rl);
  const numIn = document.createElement('input');
  numIn.type = 'number';
  numIn.placeholder = 'FILE NUMBER';
  numIn.style.cssText = 'width:36%;margin-right:3%;padding:6px;border:1px solid var(--carbon);background:transparent;font-family:inherit';
  const phraseIn = document.createElement('input');
  phraseIn.type = 'text';
  phraseIn.placeholder = 'three word phrase';
  phraseIn.style.cssText = 'width:44%;margin-right:3%;padding:6px;border:1px solid var(--carbon);background:transparent;font-family:inherit';
  const go = document.createElement('button');
  go.type = 'button';
  go.className = 'slam-btn';
  go.style.cssText = 'font-size:10px;padding:8px 10px';
  go.textContent = 'RECLAIM';
  body.append(numIn, phraseIn, go);
  go.addEventListener('click', () => {
    void (async () => {
      const number = parseInt(numIn.value, 10);
      const phrase = phraseIn.value.trim().toLowerCase();
      if (!number || !phrase) {
        bus.emit('toast', 'A number and three words. That is the entire mechanism.');
        return;
      }
      const freshId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const res = await api.reclaim(number, phrase, freshId);
      if (!res.ok || !res.data) {
        bus.emit('toast', res.memo || 'The words do not match the file.');
        return;
      }
      adoptIdentity(res.data.id);
      replaceProfile({
        uuid: res.data.id, ticket_no: number, designation: res.data.designation,
        standing: res.data.standing, grade: res.data.grade,
        form1_done: true, intake_done: true, claim_phrase: phrase,
      });
      bus.emit('toast', res.data.memo || 'The file has been transferred to this hand.');
      close();
      await cb.onReclaimed();
    })();
  });
}

/** The seismograph: standing_after over time, drawn as government print. */
function seismograph(
  series: Array<{ standing_after: number; created_at: number; reason: string }>
): HTMLElement {
  const W = 520;
  const H = 110;
  const PAD = 8;
  const vals = series.map((s) => s.standing_after);
  const lo = Math.min(...vals, 90);
  const hi = Math.max(...vals, 110);
  const x = (i: number): number => PAD + (i / Math.max(1, series.length - 1)) * (W - 2 * PAD);
  const y = (v: number): number => H - PAD - ((v - lo) / Math.max(1, hi - lo)) * (H - 2 * PAD);
  let path = '';
  series.forEach((s, i) => {
    path += `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(s.standing_after).toFixed(1)}`;
  });
  const y100 = y(100);
  const wrap = document.createElement('div');
  wrap.style.cssText = 'overflow-x:auto;margin-top:4px';
  wrap.innerHTML =
    `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" style="max-width:100%;border:1px solid rgba(28,26,23,.4);background:rgba(233,228,216,.5)">` +
    `<line x1="0" y1="${y100}" x2="${W}" y2="${y100}" stroke="#9C7E3C" stroke-width="1" stroke-dasharray="4 3"/>` +
    `<path d="${path}" fill="none" stroke="#1C1A17" stroke-width="1.6"/>` +
    series.map((s, i) =>
      `<circle cx="${x(i).toFixed(1)}" cy="${y(s.standing_after).toFixed(1)}" r="1.8" fill="${s.standing_after >= 100 ? '#1C1A17' : '#A3271E'}"><title>${s.reason}: ${s.standing_after}</title></circle>`
    ).join('') +
    `<text x="${W - PAD}" y="${y100 - 3}" text-anchor="end" font-size="8" fill="#9C7E3C" font-family="monospace">100</text>` +
    `</svg>`;
  return wrap;
}

function rule(): HTMLElement {
  const r = document.createElement('hr');
  r.className = 'rule';
  return r;
}

function esc(s: string): string {
  const d = document.createElement('div');
  d.textContent = s ?? '';
  return d.innerHTML;
}

// re-export for game.ts convenience
export { loadProfile };
